/**
 * A utility class for deduplicating concurrent requests
 * Ensures that multiple identical requests only result in one actual network call
 */
export class RequestDeduplicator {
  private pending = new Map<string, Promise<unknown>>()

  /**
   * Deduplicate a request by key
   * @param key - Unique key for the request
   * @param fn - Function that returns the request promise
   * @returns The deduplicated result
   */
  async dedupe<T>(key: string, fn: () => Promise<T>): Promise<T> {
    // Check if we already have a pending request
    const existing = this.pending.get(key)
    if (existing) {
      return existing as Promise<T>
    }

    // Create and store the promise
    const promise = fn().finally(() => {
      // Clean up after completion
      this.pending.delete(key)
    })

    this.pending.set(key, promise)
    return promise
  }

  /**
   * Clear all pending requests
   */
  clear(): void {
    this.pending.clear()
  }

  /**
   * Get the number of pending requests
   */
  get size(): number {
    return this.pending.size
  }
}
